import React, { FC, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { LoadingSpinner } from "@/components/Loading";
import { CompactPagination } from "@/components/Pagination";
import { IConsultantProfileSkill } from "@/data/consultant-profiles";
import { fetchSuggestedSkillsList, ISkill } from "@/data/skills";
import { Flex } from "@/eworkitComponents";
import { usePageableFetcher } from "@/hooks/http";

import { SuggestedSection } from "../SuggestedData/SuggestedSection";
import { SuggestedSkillsList } from "./SuggestedSkillsList";

interface ISuggestedSkillsSectionProps {
  roleId?: number;
  cvIds?: number[];
  selectedSkills: IConsultantProfileSkill[];
  onSkillClick: (skill: ISkill) => void;
  onCheckedSkillClick: (skill: ISkill) => void;
}

export const SuggestedSkillsSection: FC<ISuggestedSkillsSectionProps> = ({ roleId, cvIds, ...listProps }) => {
  const { t } = useTranslation("session");

  const suggestedFetcher = usePageableFetcher(
    params => fetchSuggestedSkillsList({ ...params, size: 15, skillRoleId: roleId, cvIds }, {}),
    [roleId, cvIds],
  );

  useEffect(() => {
    suggestedFetcher.changePage(0);
  }, [roleId, cvIds]);

  const handlePageChange = useCallback(
    (page: number) => {
      suggestedFetcher.changePage(page);
    },
    [suggestedFetcher],
  );

  const suggestedSkills = suggestedFetcher.payload?.content.map(item => item.data);

  return suggestedSkills ? (
    <SuggestedSection
      header={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.SUGGESTED_SKILLS")}
      renderPagination={() => <CompactPagination config={suggestedFetcher.payload!} onPageChange={handlePageChange} />}>
      <SuggestedSkillsList {...listProps} suggestedSkills={suggestedSkills} />
    </SuggestedSection>
  ) : (
    <Flex align="center" justify="center" grow={1} padding={["lg"]}>
      <LoadingSpinner size="lg" variant="primary" />
    </Flex>
  );
};
